import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import Badge from "../common/Badge";
import EmptyState from "../common/EmptyState";
import { getCashoutEligibility, requestCashout } from "../../services/walletService";
import { color } from "../../styles/theme";

/**
 * Cashout request modal — ported from mobile's CashDetailSheet
 * (src/Screens/cash-out-history/cash-detail-sheet): eligibility breakdown +
 * confirm, with the same NoBankDetails error branching prompting the user to
 * add bank details first.
 */
const CashoutRequestModal = ({ onClose, onRequested }) => {
  const { t } = useTranslation(["wallet", "common"]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getCashoutEligibility()
      .then((res) => setEligibility(res.data?.Result || {}))
      .catch(() => toast.error(t("cashoutRequestModal.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  const handleConfirm = async () => {
    const amount = Number(eligibility?.PromoterAmount ?? 0);
    if (amount <= 0) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestCashout();
      toast.success(res.data?.Message || t("cashoutRequestModal.requestedSuccess"));
      onRequested();
    } catch (err) {
      const data = err.response?.data;
      if (data?.Result === "NoBankDetails") {
        toast.info(data?.Message || t("cashoutRequestModal.addBankDetailsPrompt"));
        onClose();
        navigate("/wallet/bank-details");
        return;
      }
      toast.error(data?.Message || t("cashoutRequestModal.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  const hasEligibleAmount = Boolean(eligibility?.PromoterAmount);

  return (
    <Modal
      title={t("cashoutRequestModal.title")}
      onClose={onClose}
      busy={submitting}
      maxWidth={420}
      footer={!loading && (
        hasEligibleAmount ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>{t("common:actions.cancel")}</Button>
            <Button onClick={handleConfirm} disabled={submitting}>{submitting ? t("cashoutRequestModal.requesting") : t("cashoutRequestModal.requestCashout")}</Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>{t("common:actions.close")}</Button>
        )
      )}
    >
      {loading ? (
        <Loader />
      ) : hasEligibleAmount ? (
        <>
          <Row label={t("cashoutRequestModal.grossAmount")} value={eligibility.GrossAmount} />
          <Row label={t("cashoutRequestModal.netAmount")} value={eligibility.NetAmount} />
          <Row label={t("cashoutRequestModal.yourPayout")} value={eligibility.PromoterAmount} highlight />
          <Row label={t("cashoutRequestModal.platformFee")} value={eligibility.PSAmount} />
          <Row label={t("cashoutRequestModal.eligibleTransactions")} value={eligibility.EligibleTransactions} />
          <div style={{ ...s.row, borderBottom: "none" }}>
            <span style={s.rowLabel}>{t("cashoutRequestModal.payoutAccount")}</span>
            <Badge variant={eligibility.HasActivePayoutAccount ? "success" : "danger"}>
              {eligibility.HasActivePayoutAccount ? t("common:actions.yes") : t("common:actions.no")}
            </Badge>
          </div>
        </>
      ) : (
        <EmptyState text={t("cashoutRequestModal.emptyState")} />
      )}
    </Modal>
  );
};

const Row = ({ label, value, highlight = false }) => (
  <div style={s.row}>
    <span style={s.rowLabel}>{label}</span>
    <span style={{ ...s.rowValue, ...(highlight ? s.rowValueHighlight : {}) }}>{value}</span>
  </div>
);

const s = {
  row: { display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${color.gray100}`, fontSize: 13.5 },
  rowLabel: { color: color.textSecondary },
  rowValue: { fontWeight: 600, color: color.gray800 },
  rowValueHighlight: { color: color.primary600, fontSize: 15 },
};

export default CashoutRequestModal;
